import { memo, startTransition, useCallback, useDeferredValue, useEffect, useMemo, useRef } from "react";
import { titleCase } from "title-case";
import { useDebouncedCallback } from "use-debounce";
import { Callout, CalloutIntent, Card, CardSection, Center, LoadingSpinner, SearchField, Tag, VStack } from "widgets";

import { GENOMIC_TYPE_DISPLAY_NAME } from "../../settings.ts";
import type { CategoryResult, GenomeResult } from "../../store/slices/search-slice.ts";
import { useGenomeViewerStore } from "../../store/store.ts";
import type { SearchItem } from "../../types.ts";
import style from "./SearchCard.module.css";

const SearchResultItem = ({ item }: { item: SearchItem }) => {
  const navigateToFeature = useGenomeViewerStore((state) => state.navigateToFeature);
  const handleGoPress = useCallback(() => {
    navigateToFeature(item);
  }, [item, navigateToFeature]);

  return (
    <div className={style["search-result-item"]} onClick={handleGoPress}>
      <div className={style.title}>{item.name}</div>
      <div
        className={style.subtitle}
      >{`${item.seqId}: ${item.start.toLocaleString("en-US")} - ${item.stop.toLocaleString("en-US")}`}</div>
    </div>
  );
};

const CategorySection = ({ result }: { result: CategoryResult }) => {
  const { category, matchingItems } = result;
  const { categoryName, categoryType, subTypeId, totalItemCount } = category;

  const titleCased = useMemo(
    () => (subTypeId ? categoryName : titleCase(GENOMIC_TYPE_DISPLAY_NAME[categoryType])),
    [categoryType, subTypeId, categoryName],
  );

  return (
    <CardSection title={titleCased} actions={[<Tag label={totalItemCount} key={`${categoryType}-count`} />]}>
      <VStack>
        {matchingItems.map((item) => (
          <SearchResultItem key={item.id} item={{ type: categoryType, subTypeId, ...item }} />
        ))}
      </VStack>
    </CardSection>
  );
};

const GenomeResultCard = memo(function GenomeResultCard({ genomeResult }: { genomeResult: GenomeResult }) {
  return (
    <Card key={genomeResult.genomeId} noBorder title={genomeResult.genomeName} asPanelHeader className={style.card}>
      {genomeResult.categories.map((result) => (
        <CategorySection key={result.category.categoryType} result={result} />
      ))}
    </Card>
  );
});

const LoadingState = () => (
  <Center>
    <LoadingSpinner inCenter />
  </Center>
);

const ErrorState = ({ error }: { error: string }) => (
  <Callout intent={CalloutIntent.Danger} style={{ margin: 10 }}>
    {error}
  </Callout>
);

const MinCharactersState = () => <Callout style={{ margin: 10 }}>Type at least 3 characters to search</Callout>;

const NoResultsState = () => (
  <Callout intent={CalloutIntent.Info} style={{ margin: 10 }}>
    No results found
  </Callout>
);

const SearchResults = memo(function SearchResults({
  isLoading,
  error,
  searchTerm,
  results,
}: {
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  results: Map<number, GenomeResult[]> | null;
}) {
  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;
  if (searchTerm.trim().length < 3) return <MinCharactersState />;
  if (!results || results.size === 0) return <NoResultsState />;

  return (
    <>
      {Array.from(results.values())
        .flat()
        .map((genomeResult) => (
          <GenomeResultCard key={genomeResult.genomeId} genomeResult={genomeResult} />
        ))}
    </>
  );
});

export const SearchCard = () => {
  // Store selectors
  const searchTerm = useGenomeViewerStore((state) => state.searchTerm);
  const searchResults = useGenomeViewerStore((state) => state.searchResults);
  const isSearchLoading = useGenomeViewerStore((state) => state.isSearchLoading);
  const searchError = useGenomeViewerStore((state) => state.searchError);

  // Track the last term we searched for to avoid duplicate searches
  const lastSearchedTermRef = useRef<string>(searchTerm);

  // Store actions
  const setSearchTerm = useGenomeViewerStore((state) => state.setSearchTerm);
  const performSearch = useGenomeViewerStore((state) => state.performSearch);
  const abortSearch = useGenomeViewerStore((state) => state.abortSearch);

  // React UI optimizations - deferred values for better UX
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const deferredResults = useDeferredValue(searchResults);

  // Memoized search function with debouncing
  const handlePerformSearch = useCallback(
    (term: string) => {
      startTransition(() => {
        performSearch(term);
      });
    },
    [performSearch],
  );

  // Debounce with trailing only
  const debouncedPerformSearch = useDebouncedCallback(handlePerformSearch, 300, {
    trailing: true,
    leading: false,
  });

  const handleSearchChange = useCallback(
    (term: string) => {
      setSearchTerm(term);
    },
    [setSearchTerm],
  );

  // Trigger search only when search term actually changes and we haven't searched for it yet
  useEffect(() => {
    const trimmedTerm = searchTerm.trim();
    const shouldSearch = trimmedTerm.length > 2 && trimmedTerm !== lastSearchedTermRef.current;

    if (shouldSearch) {
      lastSearchedTermRef.current = trimmedTerm;
      debouncedPerformSearch(searchTerm);
    }
  }, [searchTerm, debouncedPerformSearch]);

  // Cleanup on unmount - only abort ongoing requests, don't clear state
  useEffect(() => {
    return () => {
      abortSearch(); // Only aborts the request, preserves search term and results
    };
  }, [abortSearch]);

  return (
    <Card
      noBorder
      noPadding
      actions={[
        <SearchField
          aria-label="Search for Features"
          placeholder="Search for Features"
          style={{ width: "100%" }}
          isRequired
          value={searchTerm}
          onChange={handleSearchChange}
          key="search"
        />,
      ]}
      fillHeight
      scrollable
      asPanelHeader
    >
      <SearchResults
        isLoading={isSearchLoading}
        error={searchError}
        searchTerm={deferredSearchTerm}
        results={deferredResults}
      />
    </Card>
  );
};
